import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../../config";
import CreateUserAcc from "./CreateUserAcc";
import { Link, useNavigate } from "react-router-dom";
import "./CreateUserDashboard.css";

function CreateUserPage() {
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const navigate = useNavigate();
  const permissionIndexMap = ["View", "Add", "Edit", "Delete"];

  const normalizePermissionLabels = (perms) => {
    if (Array.isArray(perms)) {
      return perms
        .map((p) => {
          if (typeof p === "number" || /^\d+$/.test(String(p))) {
            return permissionIndexMap[Number(p)];
          }
          return String(p);
        })
        .filter(Boolean);
    }

    if (perms && typeof perms === "object") {
      return Object.entries(perms)
        .filter(([, allowed]) => allowed === true)
        .map(([key]) => key);
    }

    return [];
  };

  async function fetchUsers() {
    try {
      const res = await axios.get(`${API_URL}/api/getAllAdminUser`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUsers(res.data.users || []);
    } catch {
      setUsers([]);
    }
  }

  useEffect(() => {
    fetchUsers();
  }, [showForm]);

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API_URL}/api/deleteAdminByUser/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchUsers();
    } catch {
      alert("Failed to delete user.");
    }
  };

  const roleChart = [
    { role: "superadmin", color: "linear-gradient(180deg,#60a5fa,#2563eb)" },
    { role: "manager", color: "linear-gradient(180deg,#93c5fd,#3b82f6)" },
    { role: "admin", color: "linear-gradient(180deg,#a5b4fc,#4f46e5)" },
    { role: "hr", color: "linear-gradient(180deg,#38bdf8,#0284c7)" },
    { role: "accountant", color: "linear-gradient(180deg,#7dd3fc,#0369a1)" },
  ];

  const filteredUsers =
    roleFilter === "all"
      ? users
      : users.filter((u) => (u.role || "").toLowerCase() === roleFilter);

  return (
    <div className="create-user-page">
      <div className="cu-shell">
        <section className="cu-hero">
          <div className="cu-hero-copy">
            <p className="cu-eyebrow">Access Control</p>
            <h3>Admin Panel Users</h3>
            <span>Currently managing {users.length} administrative profiles with active permissions.</span>
          </div>
          <div className="cu-hero-actions">
            <Link to="/admin/createUserSAcc" className="cu-create-btn">
              + Create New User
            </Link>
          </div>
        </section>

        <section className="cu-chart-card">
          <h6>Role Distribution (Real Data)</h6>
          <div className="cu-role-kpi-grid">
            {roleChart.map((item, i) => {
              const count = users.filter((u) => u.role === item.role).length;
              const roleLabel = item.role.charAt(0).toUpperCase() + item.role.slice(1);

              return (
                <article key={i} className="cu-role-kpi-card">
                  <p className="cu-role-kpi-label">{roleLabel}</p>
                  <h4 className="cu-role-kpi-value">{count}</h4>
                </article>
              );
            })}
          </div>
        </section>

        <section className="cu-toolbar">
          <select
            className="cu-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="superadmin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="hr">HR</option>
            <option value="accountant">Accountant</option>
          </select>
        </section>

        <section className="cu-table-card">
          {filteredUsers.length === 0 && (
            <div className="cu-empty">
              <div className="cu-empty-icon">Users</div>
              <h5>No Users Found</h5>
              <p>No users match the selected role.</p>
            </div>
          )}

          <div className="table-responsive cu-fade-in">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  {["Name", "Email", "Role", "Permissions", "Actions"].map((h, i) => (
                    <th key={i} className={h === "Actions" ? "text-center" : ""}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="cu-user-cell">
                        <div className={`cu-avatar ${user.role?.toLowerCase()}`}>{user.name?.charAt(0)}</div>
                        <span>{user.name}</span>
                      </div>
                    </td>

                    <td>{user.email}</td>
                    <td>
                      <span className={`badge ${user.role === "superadmin" ? "text-bg-danger" : "text-bg-secondary"}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <ul className="cu-perm-list">
                        {user.permissions &&
                          Object.entries(user.permissions).map(([mod, perms]) => (
                            <li key={mod}>
                              {mod}: {normalizePermissionLabels(perms).join(", ")}
                            </li>
                          ))}
                      </ul>
                    </td>

                    <td className="text-center">
                      <button
                        className="cu-icon-btn"
                        onClick={() => navigate(`/admin/updateUserPermission/${user._id}`)}
                        title="Edit"
                      >
                        Edit
                      </button>

                      <button
                        className="cu-icon-btn danger"
                        onClick={() => handleDelete(user._id)}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </section>

        {showForm && (
          <div className="modal show fade" style={{ display: "block", background: "rgba(0,0,0,0.4)" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content" style={{ borderRadius: "16px" }}>
                <div className="modal-header border-0">
                  <h5>{editUser ? "Edit User" : "Create User"}</h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowForm(false);
                      setEditUser(null);
                    }}
                  />
                </div>

                <div className="modal-body">
                  <CreateUserAcc
                    editUser={editUser}
                    closeForm={() => {
                      setShowForm(false);
                      setEditUser(null);
                    }}
                    refresh={fetchUsers}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateUserPage;


